import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { getAnnouncementsQueryOptions, viewAnnouncementMutationFn } from '@/queries/announcements';
import { AnnouncementType, AnnouncementWithViewed } from '@/queries/announcements/types';
import { useSelector } from '@/redux/helper';

const ANIMAL_ALERT_TYPE_QUERY_PARAMS = { type: ['animal-alert'] as AnnouncementType[], pageSize: 50 } as const;

const selectUnreadAnnouncementsCount = ({ list }: { list: AnnouncementWithViewed[] }) =>
  list.reduce((acc, item) => acc + (item.isViewed ? 0 : 1), 0);

export const useUnreadAnnouncementsCount = () => {
  const enableQueries = useSelector((state) => state.user.sessionChecked && state.user.logged);
  const queries = useQueries({
    queries: [
      {
        ...getAnnouncementsQueryOptions(),
        select: selectUnreadAnnouncementsCount,
        enabled: enableQueries,
      },
      {
        ...getAnnouncementsQueryOptions(ANIMAL_ALERT_TYPE_QUERY_PARAMS),
        select: selectUnreadAnnouncementsCount,
        enabled: enableQueries,
      },
    ],
  });
  const [announcementsQuery, animalAlertsQuery] = queries;
  return { data: (announcementsQuery?.data ?? 0) + (animalAlertsQuery?.data ?? 0), announcementsQuery, animalAlertsQuery };
};

export const useAnnouncementsQueries = () => {
  const enableQueries = useSelector((state) => state.user.sessionChecked && state.user.logged);
  const queries = useQueries({
    queries: [
      {
        ...getAnnouncementsQueryOptions(),
        enabled: enableQueries,
      },
      {
        ...getAnnouncementsQueryOptions(ANIMAL_ALERT_TYPE_QUERY_PARAMS),
        enabled: enableQueries,
      },
    ],
  });
  const [announcementsQuery, animalAlertsQuery] = queries;
  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);
  const data = isLoading ? undefined : [...(announcementsQuery?.data ?? []), ...(animalAlertsQuery?.data ?? [])];
  data?.sort((a, b) => Date.parse(b.publishedAt ?? '') - Date.parse(a.publishedAt ?? ''));
  return { data, isLoading, isFetching, announcementsQuery, animalAlertsQuery };
};

const updateAnnouncementViewed = (itemId: string) =>
  produce((cachedResponse: { list: AnnouncementWithViewed[] } | undefined) => {
    const cachedItem = cachedResponse?.list.find(({ _id }) => _id === itemId);
    if (cachedItem) {
      cachedItem.isViewed = true;
    }
    return cachedResponse;
  });

export const useViewAnnouncementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: viewAnnouncementMutationFn,
    onSuccess: (_, itemId) => {
      queryClient.setQueryData(getAnnouncementsQueryOptions().queryKey, updateAnnouncementViewed(itemId));
      queryClient.setQueryData(
        getAnnouncementsQueryOptions(ANIMAL_ALERT_TYPE_QUERY_PARAMS).queryKey,
        updateAnnouncementViewed(itemId),
      );
    },
  });
};
